import React, { Fragment } from 'react';

import { ModifyTask, PostTask, TaskFull } from 'interfaces/task.interface';
import { Equipment, ShortUser, dayjs } from '@umanforge/common';
import { CalendarEvent } from 'interfaces/calendar.interface';
import { Planning } from 'interfaces/planning.interface';
import { TabItem } from '@common_components/buttons/Tabs.component';
import { SectorSettings } from 'interfaces/setting.interface';
import { RoleTaskFullIds } from 'interfaces/roleTask.interface';
import { Line, Column } from 'services/chronogram.service';
import { ChronogramEvent } from './chronogramEvent.component';
import { ChronogramEventWeek } from './chronogramEventWeek.component';
import { ChronogramLineHeader } from './chronogramLineHeader';
import { ChronogramHeader } from './chronogramHeader';
import { DEFAULT_VALUES } from 'constants/calendar.constants';
import { TaskEquipment } from 'interfaces/task_equipment.interface';
import TaskModalInfo from 'features/task_modal/components/taskModalInfo.component';
import TaskModal from 'features/task_modal/components/taskModal.component';
import { useChronogramComponent } from '../hook/chronogram.hook';
import { Location } from 'interfaces/location.interface';
import { TimeGrid } from 'interfaces/timegrid.interface';
import { TaskUserUtils } from 'utils/taskUser.utils';


interface ChronogramProps {
  planning: Planning;
  tasks: TaskFull[];
  currentDate: dayjs.Dayjs;
  selectedTab: TabItem;
  settings?: SectorSettings;
  users: ShortUser[];
  roleTasks: RoleTaskFullIds[];
  locations: Location[]
  equipments: Equipment[];
  taskEquipments: TaskEquipment[];
  isWeek: boolean;
  timegrid: TimeGrid;
  timeGridUsers: Map<string, TimeGrid>;
  isAdmin?: boolean
  userToHighlight?: string;
  openTaskModal?: string;

  onTaskCreate?: ( task: PostTask ) => Promise<string> | null;
  onUpdate?: ( task: ModifyTask ) => Promise<boolean>;
  onDeleteTask?: ( taskid: string ) => void;
  refreshTask?: () => void;
  goToDay?: ( day: dayjs.Dayjs ) => void;
}

export const ChronogramComponent: React.FC<ChronogramProps> = ( {
  planning,
  tasks,
  users,
  roleTasks,
  equipments,
  locations,
  taskEquipments,
  currentDate = dayjs(),
  selectedTab,
  settings,
  isWeek,
  timegrid,
  timeGridUsers,
  isAdmin = false,
  userToHighlight,
  openTaskModal,
  onTaskCreate = ( ) => {return null; },
  onUpdate = ( ) => {return Promise.resolve( true ); },
  onDeleteTask = ( ) => {return null; },
  refreshTask = ( ) => { return null; },
  goToDay = (  ) => { return null; }
} ) => {

  const {
    columnRange,
    columns,
    isModalOpen, setIsModalOpen,
    currentEvent,
    offset,
    sortedList,
    onDragStart,
    onDragEnd,
    handleDragCaseChange,
    totalHours,
    isOutlined,
    openModal,
    triggerOverEvent,
    eventOverId,
    lineAccessor
  } = useChronogramComponent(
    tasks,
    users,
    roleTasks,
    equipments,
    locations,
    taskEquipments,
    selectedTab,
    isWeek,
    refreshTask,
    timegrid,
    timeGridUsers,
    currentDate,
    settings,
    isAdmin,
    openTaskModal
  );

  const highlightTask = ( task: TaskFull | undefined ): boolean => {
    if ( task && userToHighlight && roleTasks ) {
      return !TaskUserUtils.isUserAssignedToTaskWithRTIds( task, userToHighlight, roleTasks );
    }
    return false;
  };


  return ( <div>
    <>
      <div>
        <div className="overflow-x-auto overflow-y-visible">
          <div className="min-w-full">
            <ChronogramHeader
              columns={columns}
              center={!isWeek}
              offset={offset}
              goToDay={isWeek ? goToDay : undefined}
            />
            <Fragment>
              { sortedList.map( ( line: Line, index ) => {
                const totalHour = totalHours( line );
                const outlined = isOutlined( line );

                return <div
                  className="grid w-200 border border-gray-200 border-t-0 even:bg-[#bfdbfe65] bg-[#bfdbfe25]"
                  style={{ gridTemplateColumns: `repeat(${columns.length + offset}, minmax(0, 1fr))` }}
                  key={ `chrono_line_${index}` }
                >
                  <ChronogramLineHeader
                    totalHour={totalHour}
                    offset={offset}
                    lineName={line.name}
                  />

                  <div className="flex relative flex-row" style={{ gridColumn: `span ${columns.length}` }} onDragOver={e => { e.preventDefault(); }}>
                    <div
                      className={`grid relative w-full py-1 min-h-[2.125rem] ${ outlined ? 'bg-green-200 outline outline-green-300' : ''}`}
                      style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
                    >

                      { isWeek ?
                        line.events.map( ( event, i ) => {
                          return (
                            <ChronogramEventWeek
                              key={i}
                              event={event}
                              lineId={line.id}
                              openModal={openModal}
                              className={ `
                                ${ ( selectedTab.id == 'distribution' && eventOverId && event.id !== eventOverId ) ? 'opacity-60' : 'opacity-100' }
                                ${ highlightTask( event.task ) ? 'opacity-40' : '' }
                                transition duration-75 ease-in-out
                              ` }

                              onDragStart={ ( e: CalendarEvent ) => {
                                onDragStart( { line: line, hour: i }, e );
                              }}
                              onDragEnd={() => { onDragEnd( outlined ); }}
                              onOver={ triggerOverEvent }
                              weekStart={ currentDate }
                              draggable={ selectedTab.id == 'distribution' && isAdmin }
                              stackable={ selectedTab.id !== 'location' && selectedTab.id !== 'task'  }
                              isAdmin={isAdmin}
                            />
                          );
                        } )
                        :
                        line.events.map( ( event, i ) => {
                          return (
                            <ChronogramEvent
                              key={i}
                              event={event}
                              columnRange={columnRange}
                              celSize={ settings?.calendarStep?.value ?? DEFAULT_VALUES.STEP }
                              taskId={event.task?.id ?? ''}
                              lineId={line.id}

                              onDragStart={ ( e: CalendarEvent ) => {
                                onDragStart( { line: line, hour: 0 }, e );
                              }}
                              onDragEnd={() => { onDragEnd( outlined ); }}
                              openModal={openModal}
                              draggable={ selectedTab.id == 'distribution' && isAdmin }
                              date={currentDate}
                              onOver={ triggerOverEvent }
                              className={ `
                                ${ ( selectedTab.id == 'distribution' && eventOverId && event.id !== eventOverId ) ? 'opacity-60' : 'opacity-100' }
                                ${ highlightTask( event.task ) ? 'opacity-40' : '' }
                                transition duration-75 ease-in-out
                              ` }
                              stackable={ selectedTab.id !== 'location' && selectedTab.id !== 'task'  }
                              selectedTab={selectedTab.id}
                              isAdmin={isAdmin}
                            />
                          );
                        } )
                      }


                      {/* Cases of the chronogram ( to handle drag event ) */}
                      { columns.map( ( col: Column, i: number ) => {
                        return <div
                          key={`${line.name}_${col.name}_${i}_${index}`}
                          className={`absolute w-full h-full border-l border-slate-400/40 border-dashed ${
                            isWeek && (
                              ( dayjs( col.rawValue ).isBefore( dayjs(), 'day' ) && 'bg-red-200/20' ) ||
                              ( dayjs( col.rawValue ).isSame( dayjs(), 'day' ) && 'bg-blue-200/20' )
                            ) }
                            ${ lineAccessor( col.rawValue, line.id ) }
                          `}
                          style={{ gridColumnStart: i + 1, gridColumnEnd: i + 2 }}
                          onDragOver={e => { e.preventDefault(); }}
                          onDragEnter={ () => {
                            handleDragCaseChange( { line: line, hour: col.value } );
                          }}
                        />;
                      } )}
                    </div>
                  </div>
                </div>;
              } ) }
            </Fragment>

            { isModalOpen && (
              ( currentEvent?.task && dayjs( currentEvent?.task.dateStart ) < dayjs() ) || !isAdmin ?
                <TaskModalInfo
                  onClose={ () => { setIsModalOpen( false ); } }
                  taskId={ currentEvent?.task?.id ?? '' }
                  sectorId={ planning.idSector }
                  onActionUpdate={ refreshTask }
                /> :
                <TaskModal
                  taskId={ currentEvent?.task?.id }
                  planningId={ planning.id }
                  sectorId={ planning.idSector }
                  onClose={ () => {
                    setIsModalOpen( false );
                    refreshTask();
                  } }
                  onDeleteTask={ onDeleteTask }
                  onTaskCreate={ onTaskCreate }
                  onUpdate={ onUpdate }

                  timeStartDrag={undefined}
                  timeEndDrag={undefined}
                  dateDrag={undefined}

                  settings={settings}
                />
            ) }
          </div>
        </div>
      </div>
    </>
  </div>
  );

};
